import { FC, useEffect, useState } from "react";
import {
  Button,
  color,
  InputGroup,
  InputSelect,
  NAME_REGEX,
  NO_REGEX,
} from "@funded-here-interface/common";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import {
  countries,
  countryNameToCodeMap,
} from "@funded-here-interface/common/src/constant/country";
import {
  IndustryGroupMap,
  LegalStructuresMerchantAndSPV,
} from "../../constants/dropdownValues";
import { Industry, LegalStructure, IndustryGroup } from "../../constants/enums";
import { toast } from "react-toastify";
import { useStyles } from "../../styles/BizProfileCss";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import { formatDateDraft } from "@funded-here-interface/common/src/Utils/formatter";
import {
  getIndonesiaStateOptions,
  getStateIdByName,
} from "@funded-here-interface/common/src/constant/indonesia";
import { useGetIndonesiaCitiesQuery } from "@funded-here-interface/common/src/hooks/useGetIndonesiaCities";
import { IndonesiaCity } from "@funded-here-interface/common/src/services/getIndonesiaCities";

// Custom hooks and utilities
import { useIssuingSPVBizDraftSync } from "./hooks/useIssuingSPVBizDraftSync";
import {
  validateSPVBizProfile,
  createEmptyErrors,
  SPVBizProfileErrors,
} from "./utils/spvBizProfileValidation";

const IssuingSPVBizProfile: FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  // Redux selectors
  const {
    company: comp,
    regNumber: regno,
    dateOfIncorporation: dateOfIncorporationRedux,
    legalStructure: legalStructureRedux,
    legalStructureOthers: legalStructureOthersRedux,
    industryGroup: industryGroupRedux,
    industryDropdownValues: industryDropdownValuesRedux,
    typeOfInstitute,
    industryOthers: industryOthersRedux,
    countryISOCode: countryRedux,
    address: addressRedux,
    postal: postalRedux,
    website: websiteRedux,
    userFirstName: userFirstNameRedux,
    userLastName: userLastNameRedux,
  } = useSelector((state: RootState) => state.disBiz);
  const { userId, token } = useSelector((state: RootState) => state.auth);

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Custom hooks
  const { syncIssuingSPVBizToRedux } = useIssuingSPVBizDraftSync();

  // Page state
  const [initPage, setInitPage] = useState(true);
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);

  // Local state
  const [companyName, setCompany] = useState(comp || "");
  const [registrationNumber, setRegNumber] = useState(regno || "");
  const [dateOfIncorporation, setDateOfIncorporation] = useState(
    dateOfIncorporationRedux || "",
  );
  const [legalStructure, setLegStr] = useState(legalStructureRedux || "");
  const [isOtherLegalStructure, setIsOtherLegalStructure] = useState(
    legalStructureRedux === LegalStructure.OTHERS,
  );
  const [legalStructureOthers, setLegalStructurOthers] = useState(
    legalStructureOthersRedux || "",
  );
  const [institutionType, setInstitute] = useState(typeOfInstitute || "");
  const [industryGroup, setIndustryGroup] = useState(industryGroupRedux || "");
  const [industryDropdownValues, setIndustryDropdownValues] = useState<
    Industry[]
  >(industryDropdownValuesRedux || []);
  const [industryOthers, setIndustryOthers] = useState(
    industryOthersRedux || "",
  );
  const [country, setCountry] = useState(countryRedux || "");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState(addressRedux || "");
  const [postalcode, setPostalCode] = useState(postalRedux || "");
  const [website, setWebsite] = useState(websiteRedux || "");
  const [firstName, setUserFirstName] = useState(userFirstNameRedux || "");
  const [lastName, setUserLastName] = useState(userLastNameRedux || "");

  // country — SPV entity country of incorporation
  const isIndonesia = country === "Indonesia";

  // Indonesia cities query with caching - only fetches when stateId changes
  const {
    data: citiesData,
    isLoading: isCitiesLoading,
  } = useGetIndonesiaCitiesQuery({
    stateId: selectedStateId,
    token,
  });

  // Derive city options from cached query data
  const cityOptions = citiesData?.map((c: IndonesiaCity) => c.value) || [];

  // Error state
  const [error, setError] = useState<SPVBizProfileErrors>(createEmptyErrors());

  /**
   * Clear error for specific field
   */
  const clearError = (field: keyof SPVBizProfileErrors) => {
    setError((prev) => ({ ...prev, [field]: "" }));
  };

  /**
   * Handle legal structure change
   */
  const onLegalStructureChange = (value: LegalStructure) => {
    if (value === LegalStructure.OTHERS) {
      setIsOtherLegalStructure(true);
    } else {
      setIsOtherLegalStructure(false);
      setLegalStructurOthers("");
    }

    setLegStr(value);
    clearError("legalStructure");
  };

  /**
   * Handle industry group change
   */
  const onIndustryGroupChange = (value: IndustryGroup) => {
    setIndustryDropdownValues([]);
    setInstitute("");

    const industry: Industry[] = IndustryGroupMap[value] || [];

    setIndustryGroup(value);
    setIndustryDropdownValues([...industry]);
    clearError("industryGroup");
  };

  /**
   * Handle industry change
   */
  const onIndustryChange = (value: Industry) => {
    setInstitute(value);
    clearError("institutionType");
  };

  /**
   * Handle country change
   */
  const handleCountryChange = (value: string) => {
    clearError("country");
    setCountry(value);
    setRegion("");
    setCity("");
    setSelectedStateId(null);
  };

  /**
   * Handle region change - sets stateId which triggers cached city fetch
   */
  const handleRegionChange = (value: string) => {
    clearError("region");
    setRegion(value);
    setCity("");
    const stateId = getStateIdByName(value);
    setSelectedStateId(stateId || null);
  };

  /**
   * Handle city change
   */
  const handleCityChange = (value: string) => {
    clearError("city");
    setCity(value);
  };

  /**
   * Load draft data from backend
   */
  useEffect(() => {
    const initializeDraft = async () => {
      if (initPage) {
        try {
          const params = { userId: parseInt(userId), token };
          const data = await getOrgDraftMutation.mutateAsync(params);

          if (data) {
            setCompany(data.companyName || "");
            setRegNumber(data.companyRegistrationNo || "");
            setDateOfIncorporation(formatDateDraft(data.companyStartDate));
            setLegStr(data.legalStructure || "");
            setCountry(data.country || "");
            setRegion(data.region || "");
            setCity(data.city || "");
            setAddress(data.legalEntityAddress || "");
            setPostalCode(data.postalCode?.toString() || "");
            setWebsite(data.website || "");
            setUserFirstName(data.userFirstName || "");
            setUserLastName(data.userLastName || "");
            setIndustryGroup(data.industryGroup || "");

            if (data.industryGroup) {
              onIndustryGroupChange(data.industryGroup);
            }

            // Ensure industry is a string, not an array
            if (data.industry) {
              const industryValue = Array.isArray(data.industry)
                ? data.industry[0]
                : data.industry;
              setInstitute(industryValue);
            }

            // If Indonesia and region is set, set stateId to trigger cached city fetch
            if (data.country === "Indonesia" && data.region) {
              const stateId = getStateIdByName(data.region);
              setSelectedStateId(stateId || null);
            }
          }
        } catch (error) {
          toast.error(`Error fetching draft: ${error}`);
        } finally {
          setInitPage(false);
        }
      }
    };

    initializeDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initPage, userId, token]);

  /**
   * Save draft to backend
   * @returns Promise that resolves when save is complete
   */
  const saveDraft = async (showToast: boolean = true): Promise<void> => {
    const countryCode = countryNameToCodeMap.get(country) || "";

    // If country is not Indonesia, explicitly clear region and city
    const clearFields: ("region" | "city")[] = !isIndonesia
      ? ["region", "city"]
      : [];

    try {
      await saveOrgDraftMutation.mutateAsync({
        userId: parseInt(userId),
        token,
        userFirstName: firstName,
        userLastName: lastName,
        companyName,
        companyRegistrationNo: registrationNumber,
        companyStartDate: dateOfIncorporation || null,
        legalStructure,
        industryGroup,
        industry: institutionType,
        countryISOCode: countryCode,
        country,
        region: region || null,
        city: city || null,
        address,
        legalEntityAddress: address,
        postalCode: postalcode,
        website: website || null,
        clearFields,
      });
      if (showToast) {
        toast.success("Draft Saved");
      }
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    // Validate using safe validation function (replaces dangerous eval!)
    const validationResult = validateSPVBizProfile({
      companyName,
      registrationNumber,
      dateOfIncorporation,
      legalStructure,
      legalStructureOthers,
      industryGroup,
      institutionType,
      industryOthers,
      country,
      region,
      city,
      address,
      postalcode,
      website,
      firstName,
      lastName,
      isOtherLegalStructure,
    });

    if (!validationResult.isValid) {
      setError(validationResult.errors);
      return;
    }

    // All validation passed - save draft and wait for completion
    await saveDraft(false);

    // Sync to Redux
    syncIssuingSPVBizToRedux({
      company: companyName,
      regNumber: registrationNumber,
      dateOfIncorporation,
      legalStructure,
      legalStructureOthers,
      industryGroup,
      industryDropdownValues,
      typeOfInstitute: institutionType,
      industryOthers,
      countryISOCode: countryNameToCodeMap.get(country) || "",
      region: region || "",
      city: city || "",
      address,
      postal: postalcode,
      website,
      userFirstName: firstName.trim(),
      userLastName: lastName.trim(),
    });

    navigate("/supplier-bank-details");
  };

  return (
    <div style={{ marginTop: "70px" }}>
      <InputGroup
        isErrored={error.firstName}
        errorMessage
        onChange={(value: string) => {
          clearError("firstName");
          setUserFirstName(value.replace(NAME_REGEX, ""));
        }}
        labelText="First Name"
        id="firstName"
        value={firstName}
      />
      <InputGroup
        isErrored={error.lastName}
        errorMessage
        onChange={(value: string) => {
          clearError("lastName");
          setUserLastName(value.replace(NAME_REGEX, ""));
        }}
        labelText="Last Name"
        id="lastName"
        value={lastName}
      />
      <InputGroup
        isErrored={error.companyName}
        errorMessage
        onChange={(value: string) => {
          clearError("companyName");
          setCompany(value);
        }}
        labelText="Company Name"
        id="company"
        value={companyName}
      />
      <InputGroup
        isErrored={error.registrationNumber}
        errorMessage
        onChange={(value: string) => {
          clearError("registrationNumber");
          setRegNumber(value);
        }}
        labelText="Registration Number"
        id="registrationNumber"
        value={registrationNumber}
      />
      <InputGroup
        isErrored={error.dateOfIncorporation}
        errorMessage
        onChange={(value: string) => {
          // Prevent future dates
          if (value && new Date(value) > new Date()) {
            setError((prev) => ({
              ...prev,
              dateOfIncorporation: "Date of Incorporation cannot be in the future",
            }));
            return;
          }
          clearError("dateOfIncorporation");
          setDateOfIncorporation(value);
        }}
        labelText="Date of Incorporation"
        id="dateOfIncorporation"
        value={dateOfIncorporation}
        type="date"
        max={new Date().toISOString().split("T")[0]}
      />
      <InputSelect
        isErrored={error.legalStructure}
        errorMessage
        onChange={onLegalStructureChange}
        labelText="Legal Structure"
        id="legalStructure"
        value={legalStructure}
        data={LegalStructuresMerchantAndSPV}
      />
      {isOtherLegalStructure && (
        <InputGroup
          isErrored={error.legalStructureOthers}
          errorMessage
          onChange={(value: string) => {
            clearError("legalStructureOthers");
            setLegalStructurOthers(value);
          }}
          labelText="Please Specify Legal Structure"
          id="legalStructureOthers"
          value={legalStructureOthers}
        />
      )}
      <InputSelect
        isErrored={error.industryGroup}
        errorMessage
        onChange={onIndustryGroupChange}
        labelText="Industry Group"
        value={industryGroup}
        id="industryGroup"
        data={Object.values(IndustryGroup).sort((a, b) => a.localeCompare(b))}
      />
      <InputSelect
        isErrored={error.institutionType}
        errorMessage
        onChange={onIndustryChange}
        labelText="Industry"
        value={institutionType}
        id="institutionType"
        data={[...industryDropdownValues].sort((a, b) => a.localeCompare(b))}
      />
      <InputSelect
        isErrored={error.country}
        errorMessage
        onChange={handleCountryChange}
        labelText="Country of Incorporation"
        id="country"
        value={country}
        data={countries}
      />
      {isIndonesia && (
        <>
          <InputSelect
            isErrored={error.region}
            errorMessage
            onChange={handleRegionChange}
            labelText="Region"
            id="region"
            value={region}
            data={getIndonesiaStateOptions()}
          />
          <InputSelect
            isErrored={error.city}
            errorMessage
            onChange={handleCityChange}
            labelText={isCitiesLoading ? "City (Loading...)" : "City"}
            id="city"
            value={city}
            data={cityOptions}
            disabled={!region || isCitiesLoading}
          />
        </>
      )}

      <InputGroup
        isErrored={error.address}
        errorMessage
        onChange={(value: string) => {
          clearError("address");
          setAddress(value);
        }}
        labelText="Address"
        id="address"
        value={address}
      />
      <InputGroup
        isErrored={error.postalcode}
        errorMessage
        onChange={(value: string) => {
          clearError("postalcode");
          setPostalCode(value.replace(NO_REGEX, ""));
        }}
        labelText="Postal Code"
        id="postalcode"
        value={postalcode}
      />
      <InputGroup
        isErrored={error.website}
        errorMessage
        onChange={(value: string) => {
          clearError("website");
          setWebsite(value);
        }}
        labelText="Company Website / Online Marketplace Link"
        id="website"
        value={website}
      />
      <Button
        onClick={handleSubmit}
        textColor="#FFFF"
        backgroundColor="#338F86"
        children="Next"
        width="100%"
      />
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft(true)}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="100%"
        />
      </div>
    </div>
  );
};

export default IssuingSPVBizProfile;
